/**
 * Google Tag Manager Analytics Utility
 *
 * Provides functions to track form events via GTM dataLayer.
 *
 * Environment Detection:
 * - Currently enabled in: staging, production
 * - Debug console logs in: all environments
 * - To enable production-only: Change ENABLED_ENVIRONMENTS to ['production']
 *
 * @see google-event-tracking.md for full specification
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { GTM_EVENT_NAME, FORM_CONSTANTS, TRACKING_EVENTS, } from './trackingConstants';
// ============================================================================
// CONFIGURATION
// ============================================================================
/**
 * Environments where tracking is enabled
 *
 * INITIAL: ['staging', 'production']
 * TO CHANGE TO PRODUCTION-ONLY: Change to ['production']
 */
var ENABLED_ENVIRONMENTS = ['production'];
/**
 * Enable debug console logging
 * Set to false to disable console logs in all environments
 */
var DEBUG_ENABLED = false;
// ============================================================================
// ENVIRONMENT DETECTION
// ============================================================================
/**
 * Detects the current environment based on multiple sources
 *
 * Priority:
 * 1. window.tiofSettings.environment (if set by PHP)
 * 2. hostname detection
 * 3. Default to 'production' (safe fallback)
 */
function detectEnvironment() {
    var _a;
    // Check if environment is set via PHP settings
    if (typeof window !== 'undefined' && window.tiofSettings) {
        var settings = window.tiofSettings;
        if (settings.environment) {
            return settings.environment;
        }
    }
    // Detect from hostname
    if (typeof window !== 'undefined' && ((_a = window.location) === null || _a === void 0 ? void 0 : _a.hostname)) {
        var hostname = window.location.hostname.toLowerCase();
        // Local development
        if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('.local')) {
            return 'local';
        }
        // Staging
        if (hostname.includes('staging') || hostname.includes('stage') || hostname.includes('dev.')) {
            return 'staging';
        }
    }
    // Default to production
    return 'production';
}
/**
 * Cached environment value to avoid repeated detection
 */
var cachedEnvironment = null;
/**
 * Gets the current environment (cached after first call)
 */
function getEnvironment() {
    if (cachedEnvironment === null) {
        cachedEnvironment = detectEnvironment();
    }
    return cachedEnvironment;
}
/**
 * Checks if tracking is enabled in the current environment
 */
function isTrackingEnabled() {
    var env = getEnvironment();
    return ENABLED_ENVIRONMENTS.includes(env);
}
// ============================================================================
// DEBUG LOGGING
// ============================================================================
/**
 * Logs tracking events to console for debugging
 */
function debugLog(message, data) {
    if (!DEBUG_ENABLED)
        return;
    var env = getEnvironment();
    var enabled = isTrackingEnabled();
    console.groupCollapsed("%c[GTM Analytics]%c ".concat(message, " %c[").concat(env, "]").concat(enabled ? ' ✓' : ' ⨯'), 'color: #4285f4; font-weight: bold', 'color: #333', enabled ? 'color: #0f9d58; font-weight: bold' : 'color: #db4437; font-weight: bold');
    console.log('Environment:', env);
    console.log('Tracking Enabled:', enabled);
    console.log('Event Data:', data);
    // Show dataLayer status
    if (typeof window !== 'undefined' && window.dataLayer) {
        console.log('DataLayer:', window.dataLayer);
    }
    else if (enabled) {
        console.log('DataLayer: Not initialized yet (will be created on push)');
    }
    else {
        console.log('DataLayer: Not used (tracking disabled in this environment)');
    }
    console.groupEnd();
}
// ============================================================================
// TRACKING FUNCTIONS
// ============================================================================
/**
 * Pushes an event to the GTM dataLayer
 *
 * @param eventKey - Key from TRACKING_EVENTS constant
 * @param customData - Optional custom data to merge with event
 */
export function trackFormStep(eventKey, customData) {
    // Get event configuration
    var eventConfig = TRACKING_EVENTS[eventKey];
    if (!eventConfig) {
        console.error("[GTM Analytics] Invalid event key: ".concat(eventKey));
        return;
    }
    // Build event data
    var eventData = __assign({ event: GTM_EVENT_NAME, form_id: FORM_CONSTANTS.FORM_ID, form_name: FORM_CONSTANTS.FORM_NAME, module_name: eventConfig.module, form_step: eventConfig.step, form_step_name: eventConfig.stepName, timestamp: new Date().toISOString() }, customData);
    // Debug log (always runs for development testing)
    debugLog("Step ".concat(eventConfig.step, ": ").concat(eventConfig.stepName), eventData);
    // Check if tracking is enabled for this environment
    if (!isTrackingEnabled()) {
        if (DEBUG_ENABLED) {
            console.log("%c[GTM Analytics] Tracking disabled in ".concat(getEnvironment(), " environment"), 'color: #ff9800; font-weight: bold');
        }
        return;
    }
    // Push to dataLayer
    try {
        if (typeof window !== 'undefined') {
            // Initialize dataLayer if it doesn't exist
            window.dataLayer = window.dataLayer || [];
            // Push event
            window.dataLayer.push(eventData);
            if (DEBUG_ENABLED) {
                console.log("%c[GTM Analytics] Event pushed to dataLayer successfully", 'color: #0f9d58; font-weight: bold');
            }
        }
    }
    catch (error) {
        console.error('[GTM Analytics] Error pushing to dataLayer:', error);
    }
}
/**
 * Gets the current environment (exposed for debugging)
 */
export function getCurrentEnvironment() {
    return getEnvironment();
}
/**
 * Checks if tracking is currently enabled (exposed for debugging)
 */
export function isEnabled() {
    return isTrackingEnabled();
}
// ============================================================================
// CONVENIENCE TRACKING FUNCTIONS
// ============================================================================
/**
 * Individual tracking functions for each step (for easier imports)
 */
export var trackGetYourOffer = function () { return trackFormStep('GET_YOUR_OFFER'); };
export var trackFindYourCar = function () { return trackFormStep('FIND_YOUR_CAR'); };
export var trackManualVehicleSelection = function () { return trackFormStep('MANUAL_VEHICLE_SELECTION'); };
export var trackVehicleConfirmed = function () { return trackFormStep('VEHICLE_CONFIRMED'); };
export var trackVehicleOptionsAndPacks = function () { return trackFormStep('VEHICLE_OPTIONS_AND_PACKS'); };
export var trackCustomerDetails = function () { return trackFormStep('CUSTOMER_DETAILS'); };
export var trackVehicleConditionValuation = function () { return trackFormStep('VEHICLE_CONDITION_VALUATION'); };
export var trackBookInspection = function () { return trackFormStep('BOOK_INSPECTION'); };
export var trackNoInspectionNotReady = function () { return trackFormStep('NO_INSPECTION_NOT_READY'); };
export var trackRequestAppointment = function () { return trackFormStep('REQUEST_APPOINTMENT'); };
export var trackAppointmentConfirmed = function () { return trackFormStep('APPOINTMENT_CONFIRMED'); };
